import { useCallback, useMemo, useState } from "react";
import { Grid, Typography } from "@material-ui/core";

import {
	fieldOptionListVariableName,
	useCalculationField,
	useDispatch,
	useTemplateDebugManager,
	useVariableData
} from "@ploy-lib/calculation";
import { DploySelectionField } from "@ploy-ui/form-fields";
import { FormattedNumber, defineMessages, useIntl } from "react-intl";
import { SectionProps } from "../../section";
import { addRegisteredSectionLayout } from "../../section/sectionLayoutDescriptions";
import {
	ExtendedApplicationDebt,
	ensureCollectionHasIds
} from "./LimitedDebtField";
import { LabelComponent as Label } from "../FieldContainer";

import {
	ApplicationDebt,
	ApplicationDebtSource,
	ErrorDisplay
} from "@ploy-lib/types";
import { useFormikContext } from "formik";

const messages = defineMessages({
	whatLoansToFulfill: {
		id: "template-form.field.limitedDebtFulfillment.whatLoansToFulfill",
		defaultMessage: "Which loans should be fulfilled or paid down"
	},
	whatLoansToFulfillHelper: {
		id: "template-form.field.limitedDebtFulfillment.whatLoansToFulfillHelper",
		defaultMessage:
			"Choose which loans to fulfill or pay down. If the loan or loans you want to refinance are not in the list below, you may add them on the previous step"
	},
	loanFrom: {
		id: "template-form.field.limitedDebtFulfillment.loanFrom",
		defaultMessage: "{loanType} from {lender}"
	},
	validAccountNumberisRequired: {
		id: "template-form.field.limitedDebtFulfillment.validAccountNumberisRequired",
		defaultMessage: "A valid bank account number is required"
	},
	loanNumber: {
		id: "template-form.field.limitedDebtFulfillment.loanNumber",
		defaultMessage: "Account number"
	}
});

const LabelComponent = ({ debt }: { debt: ExtendedApplicationDebt }) => {
	const intl = useIntl();
	const loanTypes = (useVariableData(
		"Calculator",
		fieldOptionListVariableName("LoanTypeOptionHelper")
	).value ?? []) as { key: string; value: string }[];
	const localizedLoanType =
		loanTypes.find(l => l.key === debt.loanType)?.value ?? debt.loanType;
	return (
		<Grid container spacing={2} direction="column">
			<Grid container item spacing={1} justifyContent="space-between">
				<Grid item>
					<Typography style={{ fontWeight: "bold" }}>
						{intl.formatMessage(messages.loanFrom, {
							loanType: localizedLoanType,
							lender: debt.lender
						})}
					</Typography>
				</Grid>
				<Grid item style={{ flex: "1", textAlign: "right", minWidth: "unset" }}>
					<Typography style={{ fontWeight: "bold" }}>
						<FormattedNumber
							value={debt.amount}
							format="currency"
							maximumFractionDigits={0}
							minimumFractionDigits={0}
						/>
					</Typography>
				</Grid>
			</Grid>
			<Grid container item spacing={1} justifyContent="space-between">
				<Grid item>
					<Typography>{intl.formatMessage(messages.loanNumber)}</Typography>
				</Grid>
				<Grid
					item
					style={{
						textAlign: "right",
						flex: "1",
						minWidth: "unset"
					}}
				>
					<Typography>{debt.loanNumber}</Typography>
				</Grid>
			</Grid>
		</Grid>
	);
};

export function LimitedDebtsFulfillmentSection(props: SectionProps) {
	const intl = useIntl();
	const dispatch = useDispatch();

	const debtsField = useCalculationField<ApplicationDebt[] | undefined>({
		namespace: "Calculator",
		name: "Debts"
	});
	const refindebtsField = useCalculationField<ApplicationDebt[] | undefined>({
		namespace: "Calculator",
		name: "RefinancedDebts"
	});

	const debtList = useMemo(
		() => ensureCollectionHasIds(debtsField?.value),
		[debtsField?.value]
	);

	const creditPurposeField = useCalculationField<string[] | undefined>({
		namespace: "PRIVATE_CUSTOMER_DATA",
		name: "CreditPurpose"
	});
	const creditPurposeValue = creditPurposeField?.value ?? [];
	const relevantDebts = useMemo(
		() =>
			// Make sure this filter matches the filter for debts in DR: 'Debts - F85FA406-64DF-4AE1-AF6C-203A05575E43'
			debtList.filter(
				d =>
					d.loanType != null &&
					// Filter out loans from debt registry because we do not know their origin
					d.source !== ApplicationDebtSource.DebtRegistry &&
					// Also filter out existing customer engagement from backoffice
					d.source !== ApplicationDebtSource.CustomerEngagement
			) ?? [],
		[debtList]
	);

	const { enabled: debugEnabled } = useTemplateDebugManager();

	const [showHelperText, setShowHelperText] = useState(false);
	const toggleHelperText = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			setShowHelperText(show => !show);
			e.stopPropagation();
		},
		[]
	);
	const { touched } = useFormikContext();
	const isCalcFieldTouched = !!touched["Calculator"]?.["RefinancedDebts"];

	const isError = useMemo(
		() => isCalcFieldTouched && Boolean(refindebtsField.error),
		[isCalcFieldTouched, refindebtsField]
	);

	if (!creditPurposeValue.includes("REFINANCING")) {
		return <></>;
	}

	if (relevantDebts.length === 0) {
		return debugEnabled ? (
			<Grid
				container
				spacing={1}
				className={props.className}
				onClick={props.onClick}
			>
				<Grid item xs>
					<Typography>No content here, just helping debugging :)</Typography>
				</Grid>
			</Grid>
		) : null;
	}

	const relevantDebtsKeyValue = relevantDebts.map(d => {
		return {
			key: d._frontendOnlyId ?? d.applicationDebtId,
			value: <LabelComponent debt={d} />
		};
	});

	const selectedDebtsIds = relevantDebts
		.filter(d => d.refinance)
		.map(d => d._frontendOnlyId ?? d.applicationDebtId);

	return (
		<Grid
			container
			spacing={1}
			className={props.className}
			onClick={props.onClick}
		>
			<Grid item xs={12}>
				<Label
					labelText={
						<Typography variant="body2" style={{ fontWeight: "bold" }}>
							{intl.formatMessage(messages.whatLoansToFulfill)}
						</Typography>
					}
					showHelperText={showHelperText}
					toggleHelperText={toggleHelperText}
					trimmedHelperText={intl.formatMessage(
						messages.whatLoansToFulfillHelper
					)}
				/>
			</Grid>
			<DploySelectionField
				items={relevantDebtsKeyValue}
				fullWidth={true}
				value={selectedDebtsIds}
				error={isError}
				helperText={refindebtsField.error}
				errorDisplay={ErrorDisplay.Touched}
				multiple={true}
				onChange={selectedValue => {
					const updatedList = debtList.map(rd => {
						if (
							selectedValue.includes(
								rd._frontendOnlyId ?? rd.applicationDebtId
							) ||
							rd.source === ApplicationDebtSource.CustomerEngagement
						) {
							rd.refinance = true;
						} else rd.refinance = false;
						return rd;
					});
					dispatch({
						type: "patch",
						payload: {
							patches: [
								{
									target: "Debts",
									namespace: "Calculator",
									value: updatedList,
									overwrite: true,
									changeTrigger: "Debts_manualSave"
								}
							]
						}
					});
				}}
			/>
		</Grid>
	);
}

addRegisteredSectionLayout({
	displayName: "LimitedDebtsFulfillmentSection",
	name: "LimitedDebtsFulfillmentSection",
	settings: {}
});

LimitedDebtsFulfillmentSection.displayName = "LimitedDebtFulfillment";
